import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FaLightbulb,
  FaArrowRight,
  FaBolt,
} from 'react-icons/fa';

const SlideProblemOs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const points = [
    t('landing.slideProblemOs.points.fragmented', 'PDFs, slides, videos, LMS tabs, and chatbots stay disconnected.'),
    t('landing.slideProblemOs.points.answers', 'AI tools generate answers, not structured journeys people can master.'),
    t('landing.slideProblemOs.points.cost', 'Turning internal knowledge into real training still takes weeks.'),
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <section className="relative overflow-hidden py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/30 dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="absolute top-16 right-16 w-56 h-56 bg-blue-200/30 dark:bg-blue-900/15 rounded-full blur-3xl" aria-hidden />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-yellow-200/20 dark:bg-yellow-900/10 rounded-full blur-3xl" aria-hidden />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-yellow-100 dark:from-blue-900/30 dark:to-yellow-900/20 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-4 shadow-sm"
            >
              <FaLightbulb className="text-blue-600 dark:text-blue-400 text-sm" aria-hidden />
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                {t('landing.slideProblemOs.badge', 'The real problem')}
              </span>
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight tracking-tight">
              {t('landing.slideProblemOs.title', 'AI improved content. It did not solve learning.')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t(
                'landing.slideProblemOs.subtitle',
                'Coursolo turns existing knowledge into complete AI-powered learning experiences: curricula, lessons, quizzes, practice, tutoring, and progress.',
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-10 md:mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <p className="text-sm font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                {t('landing.slideProblemOs.problemLabel', 'What learners face today')}
              </p>
              <ul className="space-y-3">
                {points.map((text, i) => (
                  <li
                    key={i}
                    className="flex gap-3 p-4 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700"
                  >
                    <span className="shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <p className="text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed pt-1">{text}</p>
                  </li>
                ))}
              </ul>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed pt-2">
                {t(
                  'landing.slideProblemOs.solution',
                  'Instead of replacing educators, Coursolo gives them an AI learning operating system, so anyone can create high-quality education at scale from trusted sources.',
                )}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="overflow-hidden rounded-2xl border-2 border-slate-200 dark:border-slate-700 shadow-xl bg-white dark:bg-slate-900"
            >
              <img
                src="/Images/marketing/product-course-overview.png"
                alt={t(
                  'landing.slideProblemOs.imageAlt',
                  'Coursolo course overview with modules, learning outcomes, and syllabus',
                )}
                className="w-full h-auto object-cover object-top"
                loading="lazy"
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 md:p-6 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
          >
            <div className="flex items-start gap-3 text-center sm:text-start">
              <FaBolt className="shrink-0 mt-1 text-yellow-300" aria-hidden />
              <div>
                <p className="font-extrabold text-lg md:text-xl">
                  {t('landing.slideProblemOs.stripTitle', 'Minutes instead of weeks')}
                </p>
                <p className="text-sm text-blue-50/95 mt-0.5">
                  {t(
                    'landing.slideProblemOs.stripBody',
                    'Upload documents, presentations, websites, videos, or internal knowledge, and get interactive courses ready to teach and learn.',
                  )}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto shrink-0">
              <button
                type="button"
                onClick={() => navigate('/platform')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-blue-700 font-semibold text-sm hover:bg-blue-50 transition-colors"
              >
                {t('landing.slideProblemOs.ctaPlatform', 'See the platform')}
                <FaArrowRight className="text-xs" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border-2 border-white/40 font-semibold text-sm hover:bg-white/10 transition-colors"
              >
                {t('landing.slideProblemOs.ctaStart', 'Start free')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default SlideProblemOs;
